'use client';

import { ComponentProps, FC, useState } from 'react';
import Image from 'next/image';
import urls from '@tuic/lib/urls';
import { LocaleSwitch } from './LocaleSwitch';
import { MainNavigation } from './main-navigation';
import MeinSchiffLogo from '../../../../../ui-kit/assets/logos/mein-schiff.svg';
import TuiCruisesLogo from '../../../../../ui-kit/assets/logos/tui-cruises.svg';
import { EventClickArea, EventClickElement } from '@tuic/tracking';
import { twJoin } from 'tailwind-merge';
import { Locale } from '@tuic/i18n';
import { LegacyIcon } from '../LegacyIcon';
import { I18NFromComponent } from '../../../utils/i18n';
export type NavigationBarProps = {
  navigationItems: ComponentProps<typeof MainNavigation>['items'];
  localeSwitch?: ComponentProps<typeof LocaleSwitch>;
  locale?: Locale;
  i18n?: I18NFromComponent<typeof LocaleSwitch>;
};

/**
 * Renders the navigation bar.
 */
export const NavigationBar: FC<NavigationBarProps> = ({
  navigationItems,
  localeSwitch,
  locale,
  i18n
}) => {
  const [navigationIsOpen, setNavigationIsOpen] = useState(false);
  return <div data-track-click-area={EventClickArea.NaviMain} className="NavigationBar relative flex h-[4.375rem] max-w-[1440px] items-center gap-4 px-3 py-0 md:px-4 lg:mx-auto lg:h-30 lg:px-[3.125rem] xl:px-[4.375rem]" data-sentry-component="NavigationBar" data-sentry-source-file="navigation-bar.tsx">
      <button data-track-click-element={EventClickElement.Icon} data-track-click-label="mobile-navi" className="inline-block border-0 px-[0.375em] py-[0.25em] text-secondary-marine-100 outline-none lg:hidden" aria-label="mobile-navigation" onClick={() => setNavigationIsOpen(true)}>
        <LegacyIcon name="mobile-navi" size={40} data-sentry-element="LegacyIcon" data-sentry-source-file="navigation-bar.tsx" />
      </button>

      <a className="mt-[0.375rem] block w-[165px] lg:m-0 lg:w-3/12 lg:max-w-[350px]" aria-label="Mein Schiff" href={locale ? urls.mainLocalized.home(locale) : urls.main.home}>
        <Image data-track-click-element={EventClickElement.Image} data-track-click-label="Mein Schiff" src={MeinSchiffLogo} width={300} height={60} alt="Mein Schiff" priority data-sentry-element="Image" data-sentry-source-file="navigation-bar.tsx" />
      </a>

      <a className="block w-[106px] lg:hidden" aria-label="TUI Cruises" href={locale ? urls.mainLocalized.home(locale) : urls.main.home}>
        <Image data-track-click-element={EventClickElement.Image} data-track-click-label="TUI Cruises" src={TuiCruisesLogo} width={220} height={54} priority alt="TUI Cruises" data-sentry-element="Image" data-sentry-source-file="navigation-bar.tsx" />
      </a>

      <div className="ml-auto block lg:hidden">
        {localeSwitch && <LocaleSwitch {...localeSwitch} i18n={i18n} />}
      </div>

      <button className={twJoin('absolute right-[0.3125rem] top-[0.9375rem] z-[900] cursor-pointer appearance-none border-0 px-[0.375em] py-[0.25em] text-white outline-[none] lg:hidden', navigationIsOpen ? 'block' : 'hidden')} data-track-click-element={EventClickElement.Icon} data-track-click-label="close" onClick={() => setNavigationIsOpen(false)}>
        <LegacyIcon name="close" size={40} data-sentry-element="LegacyIcon" data-sentry-source-file="navigation-bar.tsx" />
      </button>

      <div className={twJoin('fixed inset-y-0 left-0 z-[899] w-[86vw] flex-col overflow-auto scroll-smooth bg-[#C6F1FE] lg:block lg:w-auto lg:flex-grow-[1] lg:bg-transparent lg:[position:initial]', navigationIsOpen ? 'block' : 'hidden')}>
        <MainNavigation closeNavigation={() => setNavigationIsOpen(false)} items={navigationItems} data-sentry-element="MainNavigation" data-sentry-source-file="navigation-bar.tsx" />
      </div>

      <div className={twJoin('fixed inset-0 z-[898] bg-secondary-secondary-marine-100/60 lg:hidden', navigationIsOpen ? 'block' : 'hidden')} data-track-click-element={EventClickElement.Custom} data-track-click-label="navi-overlay" onClick={() => setNavigationIsOpen(false)} />
    </div>;
};